import React, { useContext, useEffect, useMemo, useState } from 'react'
import { UserContext } from '../context/UserContext'
import DataTable from 'react-data-table-component'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import UsersForm from './UsersForm'
import NewPlayerForm from './NewPlayerForm'
import {
  Empty,
  IconBack,
  IconEdit,
  IconPlus,
  Note,
  Pill,
  Search,
  TableSkeleton,
  dayOf,
  haystack,
  useTableStyles
} from './peopleUI'
import './people.css'

/*
 * Λίστα Users — the biggest table in the admin.
 *
 * Everything the API does is untouched: GET admin/users, GET admin/sports,
 * and the same reload-on-form-close that the screen always had. What changed is
 * that the table is now readable at 3.000 rows: one sticky header, one row per
 * person with their face attached to their name, a keyword filter that runs over
 * the rows already in memory (no request), and its own horizontal scroller so a
 * wide table never drags the page sideways.
 */

export default function Users() {
  // Local, not context: a shared form flag leaked between screens.
  const [form, setForm] = useState(false)

  const [id, setId] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState([])
  const [sports, setSports] = useState([])
  const [sportID, setSportID] = useState(0)

  /* which sport tab is showing — the SportID itself, so it survives a reorder */
  const [tab, setTab] = useState(null)
  const [q, setQ] = useState('')
  const [failed, setFailed] = useState(false)

  const tableStyles = useTableStyles(true)

  const columns = useMemo(() => [
    {
      name: 'Name',
      selector: (row) => row.FullName || '',
      cell: (row) => (
        <div className="pp-face">
          <Avatar uuid={row.UUID} name={row.FullName} size={32} />
          <div className="pp-cell">
            <button
              type="button"
              className="pp-name pp-cell-line"
              title="Open profile"
              onClick={() => showForm(row.UUID, true, row.SportID)}
            >
              {row.FullName || '—'}
            </button>
            {row.Nickname ? <span className="pp-cell-sub">{row.Nickname}</span> : null}
          </div>
        </div>
      ),
      sortable: true,
      minWidth: '230px',
      grow: 2
    },
    {
      name: 'Country',
      selector: (row) => row.Country || '',
      cell: (row) => (row.Country ? row.Country : <span className="pp-dash">—</span>),
      sortable: true,
      minWidth: '140px'
    },
    {
      name: 'Registered',
      selector: (row) => dayOf(row.RegistrationDate),
      cell: (row) => <span className="pp-mono-dim">{dayOf(row.RegistrationDate) || '—'}</span>,
      sortable: true,
      width: '124px'
    },
    {
      name: 'Last login',
      selector: (row) => row.LogDate || '',
      cell: (row) => <span className="pp-mono-dim">{row.LogDate || '—'}</span>,
      sortable: true,
      minWidth: '150px'
    },
    {
      name: 'Posts',
      selector: (row) => Number(row.NumOfPosts) || 0,
      cell: (row) => <span className="pp-num">{row.NumOfPosts || 0}</span>,
      sortable: true,
      width: '92px',
      right: true
    },
    {
      name: 'Active',
      selector: (row) => (row.IsActive == 1 ? 1 : 0),
      cell: (row) => (
        row.IsActive == 1
          ? <Pill tone="good">YES</Pill>
          : <Pill tone="crit">NO</Pill>
      ),
      sortable: true,
      width: '112px'
    },
    {
      name: '',
      cell: (row) => (
        <button
          type="button"
          className="pp-rowbtn"
          title="Edit"
          aria-label={'Edit ' + (row.FullName || 'user')}
          onClick={() => showForm(row.UUID, true, row.SportID)}
        >
          <i><IconEdit /></i>
        </button>
      ),
      width: '60px',
      center: true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [])

  useEffect(() => {
    setIsLoading(true)
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/users', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setData(res.data.results)
        loadSports()
        setIsLoading(false)
      }).catch(err => {
        setIsLoading(false)
        setFailed(true)
        console.log(err)
      })
  }

  const loadSports = () => {
    setIsLoading(true)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/sports', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSports(res.data.results)
        setIsLoading(false)
      }).catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }

  const showForm = (id, a, SportID) => {
    setSportID(SportID)
    setForm(a)
    setId(id)
  }

  const rows = Array.isArray(data) ? data : []

  /* Resolved on the way through rather than in an effect, so there is never a
     frame where the sports have arrived but no tab is chosen yet. Selecting a
     tab by hand still wins, and reloading the list keeps the admin where it was. */
  const tabId = tab === null && sports.length > 0 ? sports[0].SportID : tab

  const countFor = (sid) => rows.filter((r) => r.SportID == sid).length

  const shown = useMemo(() => {
    const inSport = rows.filter((r) => r.SportID == tabId)
    const needle = q.trim().toLowerCase()
    if (!needle) return inSport
    return inSport.filter((r) =>
      haystack([r.FullName, r.Nickname, r.Country, r.Email, r.UUID]).includes(needle)
    )
  }, [rows, tabId, q])

  const activeSport = sports.filter((s) => s.SportID == tabId)[0]

  /* ---------- a new player: the create form ---------- */
  /* "Add User" opens the form with id 0. UsersForm only shows an existing
     player, so with no player behind it there was nothing to fill in. */
  if (form && id == 0) { // eslint-disable-line eqeqeq
    return (
      <div className="pp-stack">
        <NewPlayerForm
          defaultSportID={tabId}
          onCreated={loadData}
          onCancel={() => showForm(0, false, 0)}
        />
      </div>
    )
  }

  /* ---------- the form takes over the screen ---------- */
  if (form) {
    return (
      <div className="pp-stack">
        <div>
          <Btn onClick={() => showForm(0, false, 0)}>
            <IconBack /> Users List
          </Btn>
        </div>
        <UsersForm
          id={id}
          showForm={showForm}
          setForm={setForm}
          loadData={loadData}
          SportID={sportID}
        />
      </div>
    )
  }

  /* ---------- the list ---------- */
  return (
    <div className="pp-stack">
      {failed ? (
        <Note tone="crit" onDismiss={() => setFailed(false)}>
          The user list could not be loaded. Nothing has been lost — please try again.
        </Note>
      ) : null}

      <Panel
        title="Users List"
        subtitle={
          isLoading
            ? 'Loading…'
            : rows.length + (rows.length === 1 ? ' user in total' : ' users in total')
        }
        actions={
          <Btn variant="accent" onClick={() => showForm(0, true)}>
            <IconPlus /> Add User
          </Btn>
        }
      >
        {isLoading ? (
          <TableSkeleton rows={7} />
        ) : sports.length === 0 ? (
          <Empty
            title="No sports yet"
            action={<Btn variant="accent" onClick={loadData}>Try again</Btn>}
          >
            Users are grouped by sport. Once the first sport is added under
            Settings, the list appears here.
          </Empty>
        ) : (
          <>
            <div className="pp-tabs" role="tablist" aria-label="Sports">
              {sports.map((item) => (
                <button
                  type="button"
                  role="tab"
                  key={'sport' + item.SportID}
                  className="pp-tab"
                  aria-selected={item.SportID == tabId}
                  onClick={() => setTab(item.SportID)}
                >
                  {item.SportName}
                  <span className="pp-tab-n">{countFor(item.SportID)}</span>
                </button>
              ))}
            </div>

            <div className="pp-toolbar">
              <Search
                value={q}
                onChange={setQ}
                placeholder="Search by name, nickname or country…"
                label="Search users"
              />
              <span className="pp-toolbar-spacer" />
              <span className="pp-count">
                <b>{shown.length}</b> of {countFor(tabId)}
                {activeSport ? ' · ' + activeSport.SportName : ''}
              </span>
            </div>

            {shown.length === 0 ? (
              q.trim() ? (
                <Empty
                  title="No results"
                  action={<Btn onClick={() => setQ('')}>Clear search</Btn>}
                >
                  No user in this sport matches “{q.trim()}”.
                </Empty>
              ) : (
                <Empty title="No users in this sport">
                  As soon as the first user signs up{activeSport ? ' in ' + activeSport.SportName : ''},
                  they will appear here.
                </Empty>
              )
            ) : (
              <div className="pp-table">
                <DataTable
                  columns={columns}
                  data={shown}
                  customStyles={tableStyles}
                  highlightOnHover
                  persistTableHead
                  fixedHeader
                  fixedHeaderScrollHeight="62vh"
                  pagination
                  paginationPerPage={50}
                  paginationRowsPerPageOptions={[25, 50, 100, 200]}
                  noDataComponent={<Empty title="No users" />}
                />
              </div>
            )}
          </>
        )}
      </Panel>
    </div>
  )
}
